import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopNav } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { currentSeason } from "@/lib/season";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CreateRoom() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [credits, setCredits] = useState(500);
  const genKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
  const [teams, setTeams] = useState(() => [
    { key: genKey(), name: user?.name || "La mia squadra", isMine: true },
    { key: genKey(), name: "" },
    { key: genKey(), name: "" },
    { key: genKey(), name: "" },
  ]);
  const [slots, setSlots] = useState({ P: 3, D: 8, C: 8, A: 6 });
  const [presetKey, setPresetKey] = useState("classica");
  const [auctionMode, setAuctionMode] = useState("classic");
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [loading, setLoading] = useState(false);

  const addTeam = () => setTeams([...teams, { key: genKey(), name: "" }]);
  const removeTeam = (i) => setTeams(teams.filter((_, idx) => idx !== i));
  const setTeamName = (i, v) => {
    const next = [...teams]; next[i] = { ...next[i], name: v }; setTeams(next);
  };
  const setMine = (i) => setTeams(teams.map((t, idx) => ({ ...t, isMine: idx === i })));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = teams.map((t) => t.name.trim()).filter(Boolean);
    const mine = teams.find((t) => t.isMine)?.name.trim();
    if (!mine || !cleaned.includes(mine)) return toast.error("Seleziona la TUA squadra");
    if (cleaned.length < 2) return toast.error("Servono almeno 2 squadre");
    setLoading(true);
    try {
      const { data } = await api.post("/rooms", {
        name,
        initial_credits: Number(credits),
        team_names: cleaned,
        creator_team_name: mine,
        slots: { P: Number(slots.P), D: Number(slots.D), C: Number(slots.C), A: Number(slots.A) },
        auction_mode: auctionMode,
        timer_seconds: Math.max(3, Math.min(300, Number(timerSeconds) || 30)),
      });
      toast.success(`Sala creata! Codice ${data.code}`);
      nav(`/rooms/${data.id}/lobby`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <TopNav />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="label-eyebrow mb-2">Setup sala d'asta</div>
        <h1 className="text-4xl font-black tracking-tight mb-2">Crea la tua lega</h1>
        <p className="text-slate-400 mb-8">Definisci le squadre partecipanti, i crediti iniziali e gli slot per ruolo. La tua squadra parteciperà come le altre.</p>

        <form onSubmit={submit} className="space-y-6" data-testid="create-room-form">
          <div className="glass rounded-2xl p-6 space-y-5">
            <div>
              <Label className="text-slate-300">Nome lega / sala</Label>
              <Input data-testid="room-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder={`Fantalega degli amici ${currentSeason()}`} className="mt-2 bg-slate-900/60 border-white/10 h-11" />
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-5">
            <div>
              <div className="label-eyebrow mb-3">Formato rosa</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="format-presets">
                {[
                  { key: "classica", label: "Classica", desc: "3·8·8·6 (25)", slots: { P: 3, D: 8, C: 8, A: 6 } },
                  { key: "mantra", label: "Mantra", desc: "3·8·8·6 (25)", slots: { P: 3, D: 8, C: 8, A: 6 } },
                  { key: "ridotta", label: "Rosa Ridotta", desc: "2·5·5·3 (15)", slots: { P: 2, D: 5, C: 5, A: 3 } },
                  { key: "estesa", label: "Estesa", desc: "3·10·10·7 (30)", slots: { P: 3, D: 10, C: 10, A: 7 } },
                ].map((preset) => {
                  const isActive = presetKey === preset.key;
                  return (
                    <button type="button" key={preset.key} data-testid={`preset-${preset.key}`}
                      onClick={() => { setSlots(preset.slots); setPresetKey(preset.key); }}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${isActive?"border-emerald-400 bg-emerald-500/10":"border-white/10 hover:border-emerald-500/40 bg-slate-900/40"}`}>
                      <div className="font-black text-sm">{preset.label}</div>
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{preset.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-slate-500">Oppure personalizza direttamente i valori qui sotto.</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-slate-300">Crediti iniziali</Label>
                <Input data-testid="room-credits-input" required type="number" min="10" value={credits} onChange={(e) => setCredits(e.target.value)} className="mt-2 bg-slate-900/60 border-white/10 h-11" />
              </div>
              {["P", "D", "C", "A"].map((r) => (
                <div key={r}>
                  <Label className="text-slate-300">Slot {r}</Label>
                  <Input data-testid={`slot-${r}-input`} type="number" min="1" value={slots[r]} onChange={(e) => { setSlots({ ...slots, [r]: e.target.value }); setPresetKey("custom"); }} className="mt-2 bg-slate-900/60 border-white/10 h-11" />
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 space-y-5">
            <div>
              <div className="label-eyebrow mb-3">Modalità d'asta</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setAuctionMode("classic")}
                        data-testid="mode-classic"
                        className={`text-left p-4 rounded-xl border-2 transition-all ${auctionMode==="classic"?"border-emerald-400 bg-emerald-500/10":"border-white/10 hover:border-emerald-500/40 bg-slate-900/40"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">🏟️</span>
                    <div className="font-black">Classica</div>
                  </div>
                  <div className="text-xs text-slate-400">Rilanci visibili in tempo reale. Il timer si azzera ad ogni offerta.</div>
                </button>
                <button type="button" onClick={() => setAuctionMode("secret")}
                        data-testid="mode-secret"
                        className={`text-left p-4 rounded-xl border-2 transition-all ${auctionMode==="secret"?"border-emerald-400 bg-emerald-500/10":"border-white/10 hover:border-emerald-500/40 bg-slate-900/40"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">🎯</span>
                    <div className="font-black">Segreta</div>
                  </div>
                  <div className="text-xs text-slate-400">Offerta unica cieca. Vince la più alta. In caso di parità, spareggio automatico.</div>
                </button>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Timer (secondi)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input data-testid="timer-seconds-input" type="number" min="3" max="300" value={timerSeconds} onChange={(e) => setTimerSeconds(e.target.value)} className="bg-slate-900/60 border-white/10 h-11 w-32 font-mono" />
                <div className="flex gap-2 flex-wrap">
                  {[10, 30, 60, 120, 300].map((s) => (
                    <button type="button" key={s} onClick={() => setTimerSeconds(s)} data-testid={`timer-preset-${s}`}
                            className="text-xs font-bold px-3 h-8 rounded-full border border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/10 text-slate-300">
                      {s < 60 ? `${s}s` : `${s/60}m`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Da 3 secondi a 5 minuti.{auctionMode==="classic" && " In modalità classica si azzera ad ogni rilancio."}</div>
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">Squadre partecipanti</div>
                <div className="text-sm text-slate-400">Seleziona quale è la <span className="text-emerald-400 font-bold">TUA</span> squadra.</div>
              </div>
              <Button type="button" data-testid="add-team-btn" variant="outline" size="sm" onClick={addTeam} className="border-emerald-500/30 hover:bg-emerald-500/10">
                <Plus className="w-4 h-4 mr-1" />Aggiungi
              </Button>
            </div>
            <div className="space-y-2">
              {teams.map((t, i) => (
                <div key={t.key} className="flex items-center gap-2" data-testid={`team-row-${i}`}>
                  <button type="button" onClick={() => setMine(i)} data-testid={`team-mine-${i}`}
                          className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center text-xs font-black transition-all ${t.isMine ? "border-emerald-400 bg-emerald-500/20 text-emerald-400" : "border-white/10 text-slate-500 hover:border-emerald-500/50"}`}>
                    {t.isMine ? "★" : ""}
                  </button>
                  <Input value={t.name} onChange={(e) => setTeamName(i, e.target.value)} placeholder={`Squadra ${i + 1}`} className="bg-slate-900/60 border-white/10 h-11" data-testid={`team-name-${i}`} />
                  {teams.length > 2 && (
                    <button type="button" onClick={() => removeTeam(i)} className="p-2 text-slate-500 hover:text-red-400" data-testid={`team-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => nav("/dashboard")}>Annulla</Button>
            <Button type="submit" data-testid="create-submit" disabled={loading} className="h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
              {loading ? "Creazione…" : "Crea sala d'asta"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
